import type { MeetStartQueryParams } from 'useCase/app/usePrepareMeet';
import { isElectron } from '@web-chat-core/ui-kit/helpers/electron';
import { MEET_BOOTSTRAP_QUERY_PARAMS } from '@web-chat-core/toolkit';
import {
    VCS_CALL_ID_STORAGE_KEY,
    VCS_CALL_INITIATOR_ID_STORAGE_KEY,
    VCS_MEET_MESSENGER_BOOTSTRAP_ROOM_KEY,
    VCS_WORKSPACE_ID_STORAGE_KEY,
} from 'consts';
import { accessRequestsSettingsStore, callPolicyStore } from 'state/store';
import { applyCallPolicyBootstrap, getRestoredCallPolicyDraftFromSession } from '../policy';
import { setCallLsInitiatorId, setCallLsPolicy } from '../callLsData';

export { MEET_BOOTSTRAP_QUERY_PARAMS };
export {
    hasPendingPrejoinCallPolicySync,
    persistCallPolicyBootstrapFromDraft,
    persistPrejoinCallPolicyDraft,
    clearPrejoinCallPolicyDraft,
    getCallPolicyBootstrapFromSession,
} from '../policy';

type BootstrapQuerySlice = Pick<
    MeetStartQueryParams,
    | 'initiatorId'
    | 'callId'
    | 'workspaceId'
    | 'fullName'
    | 'soundEnabledParam'
    | 'notificationsEnabledParam'
    | 'joinApprovalPolicy'
>;

export const isMeetBootstrapFromQuery = (
    params: Pick<MeetStartQueryParams, 'initiatorId' | 'callId' | 'workspaceId' | 'fullName'>,
): boolean => Boolean(params.initiatorId || (params.callId && params.workspaceId) || params.fullName);

export const hasMeetMessengerBootstrapInSession = (): boolean => {
    const initiatorId = sessionStorage.getItem(VCS_CALL_INITIATOR_ID_STORAGE_KEY);
    const callId = sessionStorage.getItem(VCS_CALL_ID_STORAGE_KEY);
    const workspaceId = sessionStorage.getItem(VCS_WORKSPACE_ID_STORAGE_KEY);
    return Boolean(initiatorId || (callId && workspaceId));
};

export const isMeetBootstrapSessionForRoom = (roomName: string): boolean =>
    Boolean(roomName) &&
    hasMeetMessengerBootstrapInSession() &&
    sessionStorage.getItem(VCS_MEET_MESSENGER_BOOTSTRAP_ROOM_KEY) === roomName;

/** Сохранить bootstrap из query в session Meet */
export const persistMeetBootstrapFromQuery = (roomName: string, params: BootstrapQuerySlice): void => {
    if (!roomName) return;
    sessionStorage.setItem(VCS_MEET_MESSENGER_BOOTSTRAP_ROOM_KEY, roomName);

    if (params.initiatorId) {
        sessionStorage.setItem(VCS_CALL_INITIATOR_ID_STORAGE_KEY, params.initiatorId);
        // webEntry.tsx вызывает sessionStorage.clear() при каждой загрузке - дублируем в localStorage.
        // Данные привязаны к roomName: если откроют другую комнату - устаревшие данные будут очищены.
        setCallLsInitiatorId(roomName, params.initiatorId);
    }
    if (params.callId) {
        sessionStorage.setItem(VCS_CALL_ID_STORAGE_KEY, params.callId);
    }
    if (params.workspaceId) {
        sessionStorage.setItem(VCS_WORKSPACE_ID_STORAGE_KEY, params.workspaceId);
    }

    if (params.soundEnabledParam !== null) {
        accessRequestsSettingsStore.setSound(params.soundEnabledParam !== '0');
    }
    if (params.notificationsEnabledParam !== null) {
        accessRequestsSettingsStore.setNotifications(params.notificationsEnabledParam !== '0');
    }

    if (params.joinApprovalPolicy != null) {
        applyCallPolicyBootstrap(params.joinApprovalPolicy);
        const draft = getRestoredCallPolicyDraftFromSession()!;
        callPolicyStore.set(draft);
        setCallLsPolicy(roomName, draft);
    }
};

export const stripMeetBootstrapQueryFromUrl = (): void => {
    if (typeof window === 'undefined' || isElectron) {
        return;
    }
    const url = new URL(window.location.href);
    let changed = false;
    MEET_BOOTSTRAP_QUERY_PARAMS.forEach((param) => {
        if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
            changed = true;
        }
    });
    if (changed) {
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
};
